import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EmailLogEntryDetail } from '@/api/emailLog/emailLogApi.types';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirmDialog';
import { ModalOverlay } from '@/components/ui/modalOverlay';
import { useResendEmailLogEntryMutation } from '@/hooks/useResendEmailLogEntryMutation';
import { useWorkspaceEmailLogEntryQuery } from '@/hooks/useWorkspaceEmailLogQueries';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { EmailLogEntryDetails } from './EmailLogEntryDetails';

interface EmailLogEntryModalProps {
  entryId: string;
  onClose: () => void;
}

export function EmailLogEntryModal({ entryId, onClose }: EmailLogEntryModalProps) {
  const { t } = useTranslation(['emailLog', 'common', 'errors']);
  const entryQuery = useWorkspaceEmailLogEntryQuery(entryId);
  const resendMutation = useResendEmailLogEntryMutation();
  const [isResendConfirmationOpen, setIsResendConfirmationOpen] = useState(false);
  const entry = entryQuery.data;

  function confirmResend() {
    resendMutation.mutate(entryId, {
      onSuccess: function notifyResendOutcome(resentEntry: EmailLogEntryDetail) {
        setIsResendConfirmationOpen(false);
        if (resentEntry.status === 'sent') {
          showSuccessNotification(
            t('emailLog:resend.success', { address: resentEntry.recipientAddress })
          );
          onClose();
          return;
        }
        showErrorNotification(t('emailLog:resend.failed'));
      },
      onError: function notifyResendError(resendError) {
        setIsResendConfirmationOpen(false);
        const backendMessage = resolveApiErrorMessage(resendError);
        showErrorNotification(backendMessage.length > 0 ? backendMessage : t('errors:generic'));
      },
    });
  }

  return (
    <ModalOverlay onRequestClose={onClose} ariaLabel={t('emailLog:detail.title')}>
      <div className="bg-zoowhite w-full max-w-3xl rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200 flex flex-col max-h-[90vh]">
        <div className="p-6 border-b border-zooborder flex justify-between items-center gap-3 bg-zoovpalelavender bg-opacity-30 flex-shrink-0">
          <h3 className="text-xl font-bold text-zooindigo min-w-0 break-words">
            {entry === undefined ? t('emailLog:detail.title') : entry.subject}
          </h3>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('common:actions.close')}
            onClick={onClose}
          >
            <Icon name="X" className="text-zooindigo" />
          </Button>
        </div>

        <div className="overflow-y-auto p-6 custom-scrollbar">
          {entryQuery.isLoading === true ? (
            <div className="py-16 flex justify-center">
              <LoadingSpinner className="w-7 h-7" />
            </div>
          ) : entryQuery.isError === true || entry === undefined ? (
            <QueryErrorState
              onRetry={function retryEntry() {
                entryQuery.refetch();
              }}
            />
          ) : (
            <EmailLogEntryDetails entry={entry} />
          )}
        </div>

        <div className="p-6 border-t border-zooborder flex flex-col-reverse gap-3 sm:flex-row sm:justify-end bg-zoowhite flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            {t('common:actions.close')}
          </Button>
          {entry !== undefined && entry.isResendable === true ? (
            <Button
              className="flex items-center justify-center gap-2"
              onClick={function openResendConfirmation() {
                setIsResendConfirmationOpen(true);
              }}
            >
              <Icon name="Send" /> {t('emailLog:resend.action')}
            </Button>
          ) : null}
        </div>
      </div>

      {isResendConfirmationOpen === true && entry !== undefined ? (
        <ConfirmDialog
          title={t('emailLog:resend.confirmTitle')}
          description={t('emailLog:resend.confirmDescription', {
            address: entry.recipientAddress,
          })}
          confirmLabel={t('emailLog:resend.action')}
          iconName="Send"
          isConfirming={resendMutation.isPending}
          onConfirm={confirmResend}
          onCancel={function closeResendConfirmation() {
            setIsResendConfirmationOpen(false);
          }}
        />
      ) : null}
    </ModalOverlay>
  );
}
